import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Measurement, MeasurementFormData } from '../../types';
import { api } from '../../lib/api';

// Hook to fetch all measurements
export function useMeasurements(jobId?: number, opportunityId?: number) {
  return useQuery({
    queryKey: ['measurements', jobId, opportunityId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (jobId) params.append('job', jobId.toString());
      if (opportunityId) params.append('opportunity', opportunityId.toString());
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.getAllPages<Measurement>(`/api/measurements/${queryString}`);
      // Ensure we always return an array
      const data = response.data as any;
      if (Array.isArray(data)) {
        return data;
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results;
      }
      return [];
    },
  });
}

// Hook to fetch a single measurement
export function useMeasurement(id: number) {
  return useQuery({
    queryKey: ['measurement', id],
    queryFn: async () => {
      const response = await api.get(`/api/measurements/${id}/`);
      return response.data as Measurement;
    },
    enabled: !!id,
  });
}

// Hook to create a measurement
export function useCreateMeasurement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: MeasurementFormData) => {
      const response = await api.post('/api/measurements/', data);
      return response.data as Measurement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
  });
}

// Hook to update a measurement
export function useUpdateMeasurement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<MeasurementFormData> }) => {
      const response = await api.patch(`/api/measurements/${id}/`, data);
      return response.data as Measurement;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
      queryClient.invalidateQueries({ queryKey: ['measurement', data.id] });
    },
  });
}

// Hook to delete a measurement
export function useDeleteMeasurement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/measurements/${id}/`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
  });
}